import { Flex, Image, Text, Tooltip } from '@byecode/ui'
import { CALENDAR_MODE } from '@lighthouse/core'
import { CalendarFieldBlockThumbImageMap } from '@lighthouse/shared'
import type { AnyArray } from 'immer/dist/internal'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import type { FieldInputFormSlice } from '../formSlice'

interface CalendarModeProps {}

interface StyleSwitcherGridProps {
    options: AnyArray
    value?: string
    onChange?: (value: string) => void
}

interface StyleSwitcherItemProps {
    image: string
    label?: string
    color: string
    background: string
    checked?: boolean
    onCheck?: () => void
    disabled?: boolean
}

const SCxContainer = styled.div`
    padding: 12px 16px;
`

const SCxStyleSwitchers = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-row-gap: 8px;
    gap: 10px;
    padding: 8px 0;
`

const SCxItemContainer = styled(Flex)`
    width: 100%;
`

const SCxBlock = styled.div<Pick<React.CSSProperties, 'background'>>`
    width: 100%;
    height: 80px;
    border-radius: 16px;
    cursor: pointer;
    display: flex;
    gap: 8px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${({ background }) => background || 'var(--color-gray-50)'};

    &:hover {
        background-color: ${({ background }) => !background && 'var(--color-gray-100)'};
    }
`

const OPTIONS = [
    {
        label: '周日历',
        value: CALENDAR_MODE.week,
        image: CalendarFieldBlockThumbImageMap[CALENDAR_MODE.week],
        color: 'var(--color-main)',
        background: 'var(--color-purple-100)'
    },
    {
        label: '月日历',
        value: CALENDAR_MODE.month,
        image: CalendarFieldBlockThumbImageMap[CALENDAR_MODE.month],
        color: 'var(--color-main)',
        background: 'var(--color-purple-100)'
    }
]

const StyleSwitcherItem: React.FC<StyleSwitcherItemProps> = ({ disabled, image, label, color, background, checked, onCheck }) => {
    const hoverColor = checked ? color : 'var(--color-gray-400)'
    const activeBackground = checked ? background : undefined
    return (
        <Tooltip title={label} disabled={disabled}>
            <SCxItemContainer direction="column" alignItems="center" justifyContent="center" gap={6}>
                <SCxBlock
                    background={activeBackground}
                    style={{ cursor: disabled ? 'not-allowed' : undefined }}
                    onClick={disabled ? undefined : onCheck}
                >
                    <Image fit="cover" style={{ zIndex: 2, position: 'relative' }} radius={16} src={image} />
                </SCxBlock>
                <Text size={14} color={hoverColor}>
                    {label}
                </Text>
            </SCxItemContainer>
        </Tooltip>
    )
}

export const StyleSwitcherGrid: React.FC<StyleSwitcherGridProps> = ({ value, options, onChange }) => {
    const disabledWithVersion = useIsDisabledWithVersion()
    return (
        <SCxStyleSwitchers>
            {options.map(option => (
                <StyleSwitcherItem
                    key={option.value}
                    disabled={disabledWithVersion}
                    checked={option.value === value}
                    {...option}
                    onCheck={() => onChange?.(option.value)}
                />
            ))}
        </SCxStyleSwitchers>
    )
}

export const CalendarMode: React.FC<CalendarModeProps> = () => {
    const { control } = useFormContext<FieldInputFormSlice>()
    return (
        <SCxContainer>
            <Text>类型</Text>
            <Controller
                control={control}
                name="calendar.mode"
                // 兼容之前的月日历
                defaultValue="month"
                render={({ field }) => (
                    <StyleSwitcherGrid
                        options={OPTIONS}
                        value={field.value}
                        onChange={val => {
                            field.onChange(val)
                        }}
                    />
                )}
            />
        </SCxContainer>
    )
}
